import { PlusOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import { Button, Segmented, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeFormModal } from "@/modules/employees/components/EmployeeFormModal";
import { TemporaryPasswordModal } from "@/modules/employees/components/TemporaryPasswordModal";
import {
	useCreateEmployee,
	useEmployeesList,
	usePositionOptions,
} from "@/modules/employees/hooks/useEmployees";
import {
	type EmployeeCreatedResult,
	type EmployeeFormValues,
	type EmployeeListItem,
	HR_STATUS_COLORS,
	hrStatusLabel,
} from "@/modules/employees/types";
import { toCreateEmployeeInput } from "@/modules/employees/utils/createEmployeeInput";
import { DataTable } from "@/shared/components/DataTable";
import { usePermission } from "@/shared/hooks/usePermission";

type Qamrov = "direct" | "with";

const CHIZIQCHA = <span className="text-[#8a929e]">—</span>;

interface DepartmentEmployeesTabProps {
	departmentId: string;
	/** Arxivlangan bo'limga xodim biriktirib bo'lmaydi — «Xodim qo'shish» chizilmaydi. */
	isActive?: boolean;
}

/**
 * Bo'lim profilining "Xodimlar" tabi — `GET /employees?departmentId=` va
 * "Bo'linmalar bilan" tanlanganda `includeDescendants=true` (spec §8).
 *
 * «Xodim qo'shish» (14.09.2026): mavjud `EmployeeFormModal` ni bo'lim
 * QULFLANGAN holda ochadi — lavozimlar shu bo'lim darajasining rosteriga
 * filtrlanadi. Tugma `employees:create` ruxsati va faol bo'lim sharti bilan.
 *
 * ⚠️ `key={departmentId}` MAJBURIY: boshqa bo'limga o'tish AYNI marshrut
 * (`/departments/$id`), router komponentni qayta mount QILMAYDI. Tugma
 * holati va `usePaginatedQuery` sahifasi ichki `useState` da — kalitsiz yangi
 * bo'lim oldingisining "Bo'linmalar bilan" holati va N-sahifasi bilan ochilib,
 * bo'sh jadval berishi mumkin edi. Kalit jadvalga emas, ICHKI komponentga
 * qo'yiladi — sahifa holati aynan shu komponentning hookida.
 */
export function DepartmentEmployeesTab({
	departmentId,
	isActive = true,
}: DepartmentEmployeesTabProps) {
	return (
		<DepartmentEmployeesTable key={departmentId} departmentId={departmentId} isActive={isActive} />
	);
}

function DepartmentEmployeesTable({
	departmentId,
	isActive,
}: {
	departmentId: string;
	isActive: boolean;
}) {
	const { t } = useTranslation("departments");
	const { t: tEmployees } = useTranslation("employees");
	const { can } = usePermission();
	const [includeDescendants, setIncludeDescendants] = useState(false);
	const [formOpen, setFormOpen] = useState(false);
	const [createdResult, setCreatedResult] = useState<EmployeeCreatedResult | null>(null);
	const query = useEmployeesList({ departmentId, includeDescendants });
	const createEmployee = useCreateEmployee();

	// Lavozim NOMI — ro'yxat javobida faqat `positionId` bor
	// (`EmployeesPage` bilan bir xil manba).
	const { data: positions = [] } = usePositionOptions();
	const positionNameById = useMemo(
		() => new Map(positions.map((p) => [p.id, p.name])),
		[positions]
	);

	const qoshish = can("employees", "create") && isActive;

	const handleSubmit = (values: EmployeeFormValues) => {
		// ⚠️ Bo'lim formadan EMAS, shu tabdan — Select qulflangan bo'lsa ham
		// yuboriladigan qiymat bitta manbadan kelsin.
		const input = toCreateEmployeeInput({ ...values, departmentId });
		if (!input) {
			return;
		}
		createEmployee.mutate(input, {
			onSuccess: (result) => {
				setFormOpen(false);
				setCreatedResult(result);
			},
		});
	};

	const columns: ColumnsType<EmployeeListItem> = [
		{
			title: t("employeesTab.columns.name"),
			key: "fullName",
			render: (_, employee) => (
				<Link
					to="/employees/$id"
					params={{ id: employee.id }}
					className="text-[#1c5cab] hover:underline"
				>
					{employee.fullName}
				</Link>
			),
		},
		{
			title: t("employeesTab.columns.position"),
			key: "positionId",
			render: (_, employee) => {
				const name = employee.positionId ? positionNameById.get(employee.positionId) : undefined;
				return name ?? CHIZIQCHA;
			},
		},
		{
			title: t("employeesTab.columns.status"),
			dataIndex: "hrStatus",
			key: "hrStatus",
			render: (status: EmployeeListItem["hrStatus"]) => (
				<Tag color={HR_STATUS_COLORS[status]}>{hrStatusLabel(tEmployees, status)}</Tag>
			),
		},
	];

	return (
		<div>
			<div className="mb-4 flex flex-wrap items-center justify-between gap-2">
				<Segmented<Qamrov>
					value={includeDescendants ? "with" : "direct"}
					onChange={(value) => {
						setIncludeDescendants(value === "with");
						// ⚠️ `usePaginatedQuery` sahifani O'Z `useState` ida saqlaydi —
						// usiz 2-sahifadan almashtirilganda so'rov yangi ro'yxatning
						// 2-sahifasiga tushardi (`EmployeesPage` filtr tuzog'i).
						query.setPage(1);
					}}
					options={[
						{ value: "direct", label: t("employeesTab.directOnly") },
						{ value: "with", label: t("employeesTab.withDescendants") },
					]}
				/>
				{qoshish ? (
					<Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
						{t("employeesTab.addButton")}
					</Button>
				) : null}
			</div>
			<DataTable<EmployeeListItem> columns={columns} query={query} />

			{/* ⚠️ Faqat ochilganda mount qilinadi — forma rahbar qidiruvi
			    (`/employees?q=`) va bo'limlar ro'yxatini so'raydi; tabni shunchaki
			    ko'rayotgan foydalanuvchi uchun bu ortiqcha so'rovlar bo'lardi. */}
			{formOpen ? (
				<EmployeeFormModal
					open
					editing={null}
					lockedDepartmentId={departmentId}
					isSubmitting={createEmployee.isPending}
					onCancel={() => setFormOpen(false)}
					onSubmit={handleSubmit}
				/>
			) : null}
			<TemporaryPasswordModal result={createdResult} onClose={() => setCreatedResult(null)} />
		</div>
	);
}
